import { MainCategory, SubCategory } from '@flows.cash/types'

// Define all available Lucide icon names that we use
export const AVAILABLE_CATEGORY_ICONS = [
  'Armchair',
  'Award',
  'Baby',
  'Banknote',
  'BarChart',
  'Bed',
  'Bird',
  'Bone',
  'Book',
  'BookOpen',
  'Brain',
  'Briefcase',
  'Brush',
  'Building',
  'Building2',
  'Bus',
  'Camera',
  'Car',
  'Cat',
  'CheckCircle',
  'CircleDollarSign',
  'CircleDot',
  'Clapperboard',
  'Clock',
  'Coffee',
  'CreditCard',
  'DollarSign',
  'Dog',
  'Droplet',
  'Droplets',
  'Dumbbell',
  'Eye',
  'FileText',
  'Film',
  'Fish',
  'Flame',
  'Folder',
  'Fuel',
  'Gamepad',
  'Gamepad2',
  'Gift',
  'Glasses',
  'GraduationCap',
  'Hammer',
  'HandHeart',
  'Heart',
  'HeartPulse',
  'Home',
  'Hotel',
  'Key',
  'Keyboard',
  'Lamp',
  'Landmark',
  'Laptop',
  'Library',
  'Lightbulb',
  'LineChart',
  'Map',
  'MapPin',
  'Milk',
  'Monitor',
  'MonitorPlay',
  'MoreHorizontal',
  'Mouse',
  'Music',
  'Navigation',
  'Package',
  'Palette',
  'PalmTree',
  'ParkingCircle',
  'Paw',
  'Percent',
  'Phone',
  'PieChart',
  'PiggyBank',
  'Pill',
  'Pizza',
  'Plane',
  'Play',
  'Plus',
  'Printer',
  'Puzzle',
  'Rabbit',
  'Radio',
  'Receipt',
  'RefreshCw',
  'Repeat',
  'School',
  'Scissors',
  'Shield',
  'ShieldCheck',
  'Ship',
  'Shirt',
  'ShoppingBag',
  'ShoppingCart',
  'Signal',
  'Smartphone',
  'Smile',
  'SmilePlus',
  'Sofa',
  'Sparkles',
  'Spray',
  'Star',
  'Stethoscope',
  'Store',
  'Tags',
  'Target',
  'Ticket',
  'Timer',
  'Tool',
  'Train',
  'TreePine',
  'TrendingUp',
  'Truck',
  'Tv',
  'UserCircle',
  'UserPlus',
  'Users',
  'Utensils',
  'UtensilsCrossed',
  'Wallet',
  'Wifi',
  'Wine',
  'Wrench',
  'Zap'
] as const

export type CategoryIconName = typeof AVAILABLE_CATEGORY_ICONS[number]

// Validate if an icon name is valid
export const isValidIconName = (iconName: string): iconName is CategoryIconName => {
  return AVAILABLE_CATEGORY_ICONS.includes(iconName as CategoryIconName)
}

// Get a fallback icon based on category type and name
export const getFallbackIcon = (type: 'income' | 'expense', name: string = ''): CategoryIconName => {
  const nameLower = name.toLowerCase()
  
  // Income-specific icons
  if (type === 'income') {
    if (nameLower.includes('salary')) return 'Wallet'
    if (nameLower.includes('business')) return 'Briefcase'
    if (nameLower.includes('investment')) return 'TrendingUp'
    if (nameLower.includes('rental')) return 'Building'
    return 'Banknote'
  }
  
  // Expense-specific icons
  if (nameLower.includes('house') || nameLower.includes('home')) return 'Home'
  if (nameLower.includes('transport')) return 'Car'
  if (nameLower.includes('food') || nameLower.includes('dining')) return 'Utensils'
  if (nameLower.includes('utilities')) return 'Zap'
  if (nameLower.includes('health')) return 'HeartPulse'
  if (nameLower.includes('education')) return 'GraduationCap'
  if (nameLower.includes('entertainment')) return 'Gamepad'
  if (nameLower.includes('shopping')) return 'ShoppingCart'
  if (nameLower.includes('travel')) return 'Plane'
  if (nameLower.includes('insurance')) return 'Shield'
  if (nameLower.includes('pet') || nameLower.includes('animal')) return 'Cat'
  
  return 'Package'
}

// Map of fallback icons for common missing icons
const iconFallbackMap: Record<string, CategoryIconName> = {
  'Paw': 'Cat',
  'Pet': 'Cat',
  'Animal': 'Cat',
  'Dog': 'Cat',
  'Fish': 'Cat',
  'Bird': 'Cat'
};

// Ensure a category has a valid icon, or assign a fallback
export const ensureValidIcon = (category: MainCategory | SubCategory): CategoryIconName => {
  if (!category.icon) {
    return getFallbackIcon(category.type, category.name);
  }
  
  // Check if the icon is in our list of valid icons
  if (!isValidIconName(category.icon)) {
    // Check if we have a specific fallback for this icon
    if (iconFallbackMap[category.icon]) {
      return iconFallbackMap[category.icon];
    }
    // Otherwise use a fallback based on category type and name
    return getFallbackIcon(category.type, category.name);
  }
  
  return category.icon as CategoryIconName;
}

// Validate and fix icons for all categories and subcategories
export const validateCategoryIcons = (categories: MainCategory[]): MainCategory[] => {
  return categories.map(category => ({
    ...category,
    icon: ensureValidIcon(category),
    subcategories: category.subcategories.map(sub => ({
      ...sub,
      icon: sub.icon && isValidIconName(sub.icon) 
        ? sub.icon 
        : getFallbackIcon(sub.type, sub.name)
    }))
  }))
}

// Get icon suggestions based on category name
export const getSuggestedIcons = (categoryName: string): CategoryIconName[] => {
  const nameLower = categoryName.toLowerCase()
  const suggestions: CategoryIconName[] = []

  // Income categories
  if (nameLower.includes('income') || nameLower.includes('salary')) {
    suggestions.push('Banknote', 'Wallet', 'DollarSign', 'CircleDollarSign')
  }
  if (nameLower.includes('business')) {
    suggestions.push('Briefcase', 'LineChart', 'TrendingUp')
  }
  if (nameLower.includes('investment')) {
    suggestions.push('TrendingUp', 'LineChart', 'PiggyBank')
  }

  // Housing & Utilities
  if (nameLower.includes('house') || nameLower.includes('home')) {
    suggestions.push('Home', 'Building', 'Building2')
  }
  if (nameLower.includes('utilities')) {
    suggestions.push('Zap', 'Droplets', 'Flame')
  }

  // Transportation
  if (nameLower.includes('transport')) {
    suggestions.push('Car', 'Bus', 'Train', 'Plane')
  }

  // Food & Dining
  if (nameLower.includes('food') || nameLower.includes('dining')) {
    suggestions.push('Utensils', 'Coffee', 'Wine')
  }
  if (nameLower.includes('grocery')) {
    suggestions.push('ShoppingCart', 'ShoppingBag')
  }

  // Health & Education
  if (nameLower.includes('health')) {
    suggestions.push('HeartPulse', 'Pill', 'Stethoscope')
  }
  if (nameLower.includes('education')) {
    suggestions.push('GraduationCap', 'Library', 'Book')
  }

  // Entertainment & Shopping
  if (nameLower.includes('entertainment')) {
    suggestions.push('Gamepad', 'Film', 'Tv', 'Play')
  }
  if (nameLower.includes('shopping')) {
    suggestions.push('ShoppingCart', 'ShoppingBag', 'Package')
  }

  // Travel & Accommodation
  if (nameLower.includes('travel') || nameLower.includes('trip')) {
    suggestions.push('Plane', 'Hotel', 'Car', 'Ticket')
  }
  if (nameLower.includes('flight')) {
    suggestions.push('Plane')
  }
  if (nameLower.includes('hotel') || nameLower.includes('accommodation')) {
    suggestions.push('Hotel', 'Building')
  }

  // Family & Personal
  if (nameLower.includes('family')) {
    suggestions.push('Users', 'Heart', 'Home')
  }
  if (nameLower.includes('personal')) {
    suggestions.push('UserCircle', 'Smile', 'Heart')
  }

  // Add household-related suggestions
  if (nameLower.includes('household') || nameLower.includes('home')) {
    suggestions.push('Sofa', 'Lamp', 'Home', 'Building')
  }
  if (nameLower.includes('furniture')) {
    suggestions.push('Sofa', 'Lamp')
  }
  if (nameLower.includes('kitchen')) {
    suggestions.push('Utensils', 'UtensilsCrossed')
  }
  if (nameLower.includes('garden') || nameLower.includes('outdoor')) {
    suggestions.push('TreePine')
  }
  if (nameLower.includes('cleaning')) {
    suggestions.push('Spray')
  }
  if (nameLower.includes('appliances')) {
    suggestions.push('Tv')
  }

  // Miscellaneous suggestions
  if (nameLower.includes('misc') || nameLower.includes('other')) {
    suggestions.push('Package', 'Folder', 'Plus')
  }
  if (nameLower.includes('fee') || nameLower.includes('charge')) {
    suggestions.push('Receipt', 'FileText')
  }
  if (nameLower.includes('donation') || nameLower.includes('charity')) {
    suggestions.push('Heart', 'Gift')
  }

  // If no specific suggestions, return some general icons
  if (suggestions.length === 0) {
    suggestions.push('Package', 'Folder', 'Plus')
  }

  return suggestions
} 